import type { ModifierOverride } from "../../app/storeTypes.ts";
import {
  mat4Compose,
  mat4Inverse,
  mat4Multiply,
} from "../../blend-reader/math/transforms.ts";
import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { evaluateCurve } from "../../modeling/curvemath.ts";
import {
  buildSharedVertexIndices,
  perVertexUVsFromLoopUVs,
} from "../../modeling/loopExpand.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import { cloneTopology } from "../../modeling/topology.ts";
import {
  resolveFaceMaterialIndices,
  type SceneObject,
} from "../../modeling/types.ts";
import { applyModifiers, modifierStackKey } from "../modifierPipeline.ts";
import { sceneObjectCacheKey } from "./cacheKeys.ts";
import type {
  ModifierCurvePath,
  ModifierEvaluationInput,
  ModifierGeometry,
  ModifierOwnerTransform,
} from "./modifierTypes.ts";
import { buildGeometryGroupsFromFaceMaterialIndices } from "./renderGeometry.ts";

/**
 * Transform a flat Float32Array of positions (x,y,z triplets) by a
 * column-major 4×4 matrix.  Used to convert target-object-local geometry
 * into the owner's local space before passing it to modifier cores.
 */
function mat4TransformPositions(
  positions: Float32Array,
  m: number[],
): Float32Array {
  const out = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i],
      y = positions[i + 1],
      z = positions[i + 2];
    out[i] = m[0] * x + m[4] * y + m[8] * z + m[12];
    out[i + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
    out[i + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
  }
  return out;
}

function mat4TransformNormals(
  normals: Float32Array | null,
  m: number[],
): Float32Array | null {
  if (!normals) return null;
  const out = new Float32Array(normals.length);
  for (let i = 0; i < normals.length; i += 3) {
    const transformed = mat4TransformDirection(
      normals[i],
      normals[i + 1],
      normals[i + 2],
      m,
    );
    const n = normalizeOr(
      transformed[0],
      transformed[1],
      transformed[2],
      [0, 1, 0],
    );
    out[i] = n[0];
    out[i + 1] = n[1];
    out[i + 2] = n[2];
  }
  return out;
}

/** Transform a direction vector by the linear part of a column-major 4x4 matrix. */
function mat4TransformDirection(
  x: number,
  y: number,
  z: number,
  m: number[],
): [number, number, number] {
  return [
    m[0] * x + m[4] * y + m[8] * z,
    m[1] * x + m[5] * y + m[9] * z,
    m[2] * x + m[6] * y + m[10] * z,
  ];
}

function vec3Length(x: number, y: number, z: number): number {
  return Math.sqrt(x * x + y * y + z * z);
}

function normalizeOr(
  x: number,
  y: number,
  z: number,
  fallback: readonly [number, number, number],
): [number, number, number] {
  const len = vec3Length(x, y, z);
  if (len < 1e-8) return [fallback[0], fallback[1], fallback[2]];
  return [x / len, y / len, z / len];
}

function projectNormalOntoTangent(
  raw: readonly [number, number, number],
  tangent: readonly [number, number, number],
  fallback: readonly [number, number, number],
): [number, number, number] {
  let nx =
    raw[0] -
    tangent[0] *
      (raw[0] * tangent[0] + raw[1] * tangent[1] + raw[2] * tangent[2]);
  let ny =
    raw[1] -
    tangent[1] *
      (raw[0] * tangent[0] + raw[1] * tangent[1] + raw[2] * tangent[2]);
  let nz =
    raw[2] -
    tangent[2] *
      (raw[0] * tangent[0] + raw[1] * tangent[1] + raw[2] * tangent[2]);
  let len = vec3Length(nx, ny, nz);
  if (len >= 1e-8) return [nx / len, ny / len, nz / len];

  nx =
    fallback[0] -
    tangent[0] *
      (fallback[0] * tangent[0] +
        fallback[1] * tangent[1] +
        fallback[2] * tangent[2]);
  ny =
    fallback[1] -
    tangent[1] *
      (fallback[0] * tangent[0] +
        fallback[1] * tangent[1] +
        fallback[2] * tangent[2]);
  nz =
    fallback[2] -
    tangent[2] *
      (fallback[0] * tangent[0] +
        fallback[1] * tangent[1] +
        fallback[2] * tangent[2]);
  len = vec3Length(nx, ny, nz);
  if (len >= 1e-8) return [nx / len, ny / len, nz / len];

  const ref: [number, number, number] =
    Math.abs(tangent[0]) <= Math.abs(tangent[1]) &&
    Math.abs(tangent[0]) <= Math.abs(tangent[2])
      ? [1, 0, 0]
      : Math.abs(tangent[1]) <= Math.abs(tangent[2])
        ? [0, 1, 0]
        : [0, 0, 1];
  nx =
    ref[0] -
    tangent[0] *
      (ref[0] * tangent[0] + ref[1] * tangent[1] + ref[2] * tangent[2]);
  ny =
    ref[1] -
    tangent[1] *
      (ref[0] * tangent[0] + ref[1] * tangent[1] + ref[2] * tangent[2]);
  nz =
    ref[2] -
    tangent[2] *
      (ref[0] * tangent[0] + ref[1] * tangent[1] + ref[2] * tangent[2]);
  return normalizeOr(nx, ny, nz, [1, 0, 0]);
}

function buildCurveTargetPath(
  ownerInverseMatrix: number[],
  targetObject: SceneObject,
): ModifierCurvePath | null {
  if (!targetObject.curveData) return null;

  const evaluation = evaluateCurve(targetObject.curveData);
  const sampleCount = evaluation.frames.length;
  if (sampleCount === 0) return null;

  const targetMatrix = mat4Compose(
    targetObject.position,
    targetObject.rotation,
    targetObject.scale,
  );
  const targetToOwner = mat4Multiply(ownerInverseMatrix, targetMatrix);

  const positions = new Float32Array(sampleCount * 3);
  const tangents = new Float32Array(sampleCount * 3);
  const normals = new Float32Array(sampleCount * 3);
  const normalScales = new Float32Array(sampleCount);
  const binormalScales = new Float32Array(sampleCount);
  const radii = new Float32Array(sampleCount);
  const cumulativeLengths = new Float32Array(sampleCount);

  for (let i = 0; i < sampleCount; i++) {
    const frame = evaluation.frames[i];
    const posOff = i * 3;
    const fx = frame.position[0];
    const fy = frame.position[1];
    const fz = frame.position[2];
    positions[posOff] =
      targetToOwner[0] * fx +
      targetToOwner[4] * fy +
      targetToOwner[8] * fz +
      targetToOwner[12];
    positions[posOff + 1] =
      targetToOwner[1] * fx +
      targetToOwner[5] * fy +
      targetToOwner[9] * fz +
      targetToOwner[13];
    positions[posOff + 2] =
      targetToOwner[2] * fx +
      targetToOwner[6] * fy +
      targetToOwner[10] * fz +
      targetToOwner[14];

    const tangentRaw = mat4TransformDirection(
      frame.tangent[0],
      frame.tangent[1],
      frame.tangent[2],
      targetToOwner,
    );
    const tangent = normalizeOr(
      tangentRaw[0],
      tangentRaw[1],
      tangentRaw[2],
      [0, 1, 0],
    );
    tangents[posOff] = tangent[0];
    tangents[posOff + 1] = tangent[1];
    tangents[posOff + 2] = tangent[2];

    const normalRaw = mat4TransformDirection(
      frame.normal[0],
      frame.normal[1],
      frame.normal[2],
      targetToOwner,
    );
    const binormalRaw = mat4TransformDirection(
      frame.binormal[0],
      frame.binormal[1],
      frame.binormal[2],
      targetToOwner,
    );
    const normal = projectNormalOntoTangent(normalRaw, tangent, binormalRaw);
    normals[posOff] = normal[0];
    normals[posOff + 1] = normal[1];
    normals[posOff + 2] = normal[2];

    normalScales[i] = Math.max(
      vec3Length(normalRaw[0], normalRaw[1], normalRaw[2]),
      1e-6,
    );
    binormalScales[i] = Math.max(
      vec3Length(binormalRaw[0], binormalRaw[1], binormalRaw[2]),
      1e-6,
    );
    radii[i] = evaluation.radii[i] ?? 1;

    if (i > 0) {
      const prev = posOff - 3;
      const dx = positions[posOff] - positions[prev];
      const dy = positions[posOff + 1] - positions[prev + 1];
      const dz = positions[posOff + 2] - positions[prev + 2];
      cumulativeLengths[i] =
        cumulativeLengths[i - 1] + Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
  }

  return {
    positions,
    tangents,
    normals,
    normalScales,
    binormalScales,
    radii,
    cumulativeLengths,
    cyclic: targetObject.curveData.cyclic,
  };
}

const _sceneObjectNameMapCache = new WeakMap<
  ReadonlyMap<string, SceneObject>,
  Map<string, SceneObject>
>();

export function buildSceneObjectNameMap(
  sceneObjects: ReadonlyMap<string, SceneObject>,
): Map<string, SceneObject> {
  const cached = _sceneObjectNameMapCache.get(sceneObjects);
  if (cached) return cached;

  const byName = new Map<string, SceneObject>();
  for (const obj of sceneObjects.values()) {
    byName.set(obj.name, obj);
  }
  _sceneObjectNameMapCache.set(sceneObjects, byName);
  return byName;
}

function sceneObjectCacheFragment(
  obj: SceneObject,
  sceneObjectCacheKeys?: ReadonlyMap<string, string>,
): string {
  return sceneObjectCacheKeys?.get(obj.name) ?? sceneObjectCacheKey(obj);
}

function sceneObjectVariantCacheFragment(
  obj: SceneObject,
  variant: string,
  sceneObjectCacheKeys?: ReadonlyMap<string, string>,
): string {
  return `${sceneObjectCacheFragment(obj, sceneObjectCacheKeys)}:${variant}`;
}

/**
 * Resolve a vertex-group id on the owner mesh into a sorted flat index array.
 * Returns null when the id is unset, the group is missing (e.g. was deleted
 * after the modifier was added), or the group is empty. Modifier cores treat
 * null as "apply to the whole mesh", matching Blender's behavior.
 */
function resolveVertexGroupIndices(
  owner: ModifierOwnerTransform,
  vertexGroupId: string | null,
): Uint32Array | null {
  if (!vertexGroupId || !owner.vertexGroups) return null;
  const group = owner.vertexGroups.find((g) => g.id === vertexGroupId);
  if (!group || group.indices.size === 0) return null;
  const arr = new Uint32Array(group.indices.size);
  let i = 0;
  for (const idx of group.indices) arr[i++] = idx;
  arr.sort();
  return arr;
}

export function mergeModifierStack(
  baseModifiers: readonly BlendModifier[],
  overrides: readonly ModifierOverride[] | undefined,
): BlendModifier[] {
  const objOverrides = overrides ?? [];
  const maxLen = Math.max(baseModifiers.length, objOverrides.length);
  const modifiers: BlendModifier[] = [];

  for (let idx = 0; idx < maxLen; idx++) {
    const base = baseModifiers[idx];
    const override = objOverrides[idx];
    let merged = override?.runtimeModifier ?? base;
    if (!merged) continue;
    if (override && (override.enabled !== undefined || override.props)) {
      merged = { ...merged, ...(override.props ?? {}) } as BlendModifier;
      if (override.enabled !== undefined)
        merged = { ...merged, enabled: override.enabled };
    }
    modifiers.push(merged);
  }

  return modifiers;
}

export function modifierStackCanGenerateGeometryWithoutBase(
  modifiers: readonly BlendModifier[],
): boolean {
  return modifiers.some(
    (modifier) => modifier.enabled && modifier.kind === "instance",
  );
}

/**
 * True when any enabled modifier consults sibling scene objects (array offset,
 * instance source, shrinkwrap/curve/boolean target). Such stacks need the
 * dependency map populated for the worker; pure standalone stacks (subsurf,
 * mirror, bevel, …) can pass an empty dependency snapshot.
 */
export function modifierStackNeedsSceneContext(
  modifiers: readonly BlendModifier[],
): boolean {
  return modifiers.some((modifier) => {
    if (!modifier.enabled) return false;
    switch (modifier.kind) {
      case "array":
        return !!modifier.offsetObjectName;
      case "instance":
        return !!modifier.sourceObjectName;
      case "shrinkwrap":
      case "curve":
      case "boolean":
        return !!modifier.targetObjectName;
      default:
        return false;
    }
  });
}

/**
 * True when any enabled modifier needs the owner's world transform to map
 * sibling-object geometry into owner-local space. When false, owner identity
 * is sufficient and yields cache stability under owner placement edits.
 */
export function modifierStackNeedsOwnerWorldTransform(
  modifiers: readonly BlendModifier[],
): boolean {
  return modifiers.some((modifier) => {
    if (!modifier.enabled) return false;
    switch (modifier.kind) {
      case "array":
        return !!modifier.offsetObjectName;
      case "instance":
        return modifier.orientation === "global";
      case "shrinkwrap":
      case "curve":
      case "boolean":
        return !!modifier.targetObjectName;
      default:
        return false;
    }
  });
}

export function modifierStackHasEnabledInstance(
  modifiers: readonly BlendModifier[],
): boolean {
  return modifiers.some(
    (modifier) => modifier.enabled && modifier.kind === "instance",
  );
}

export function buildSceneObjectModifierGeometry(
  obj: SceneObject,
): ModifierGeometry {
  const vertexCount = obj.vertices.length / 3;
  const topology = cloneTopology(obj.topology);
  return {
    positions: new Float32Array(obj.vertices),
    indices: buildSharedVertexIndices(obj.indices, obj.topology),
    normals: null,
    uvs:
      obj.uvs && obj.uvs.length > 0
        ? perVertexUVsFromLoopUVs(obj.uvs, obj.topology, vertexCount)
        : null,
    morphArrays: [],
    skinIndex: null,
    skinWeight: null,
    sculptDisplacement: null,
    groups: [],
    faceAttributes: obj.faceAttributes,
    edges: topology.edges.length > 0 ? topology.edges : null,
    edgeFlags: topology.edges.length > 0 ? topology.edgeFlags : null,
    edgeCreases:
      topology.edges.length > 0 ? (topology.edgeCreases ?? null) : null,
    skinRadii: obj.skinRadii ? new Float32Array(obj.skinRadii) : null,
    topology,
    // Initial provenance is identity — the modifier stack will compose per-step
    // provenances against this as it runs, so the final geometry carries the
    // canonical source-object → baked-output mapping.
    provenance: identityProvenance(vertexCount),
  };
}

export interface ModifierEvaluationDependencies {
  sceneObjectsByName: Map<string, SceneObject>;
  modifierOverrides: Map<string, readonly ModifierOverride[]>;
  sceneObjectCacheKeys: Map<string, string>;
}

/**
 * Collect the minimal scene context needed for worker-side modifier
 * evaluation.
 *
 * The main thread only discovers dependency objects and their override stacks;
 * the worker remains responsible for resolving runtime modifier inputs and
 * evaluating dependency stacks.
 */
export function collectModifierEvaluationDependencies(
  modifiers: readonly BlendModifier[],
  sceneObjects: ReadonlyMap<string, SceneObject>,
  sceneObjectModifierOverrides?: ReadonlyMap<
    string,
    readonly ModifierOverride[]
  >,
): ModifierEvaluationDependencies {
  const collectedObjects = new Map<string, SceneObject>();
  const collectedOverrides = new Map<string, readonly ModifierOverride[]>();
  const collectedCacheKeys = new Map<string, string>();
  const visitedStacks = new Set<string>();

  const includeObject = (name: string): SceneObject | null => {
    const obj = sceneObjects.get(name);
    if (!obj) return null;
    if (!collectedObjects.has(name)) {
      collectedObjects.set(name, obj);
      collectedCacheKeys.set(name, sceneObjectCacheKey(obj));
      const overrides = sceneObjectModifierOverrides?.get(obj.id);
      if (overrides && overrides.length > 0) {
        collectedOverrides.set(obj.id, overrides);
      }
    }
    return obj;
  };

  const visitStack = (stack: readonly BlendModifier[]): void => {
    for (const modifier of stack) {
      switch (modifier.kind) {
        case "array":
          if (modifier.offsetObjectName)
            includeObject(modifier.offsetObjectName);
          break;
        case "instance": {
          if (!modifier.sourceObjectName) break;
          const sourceObject = includeObject(modifier.sourceObjectName);
          if (!sourceObject || visitedStacks.has(sourceObject.name)) break;
          visitedStacks.add(sourceObject.name);
          const overrides = sceneObjectModifierOverrides?.get(sourceObject.id);
          if (!overrides || overrides.length === 0) break;
          visitStack(mergeModifierStack([], overrides));
          break;
        }
        case "shrinkwrap":
          if (modifier.targetObjectName)
            includeObject(modifier.targetObjectName);
          break;
        case "curve":
          if (modifier.targetObjectName)
            includeObject(modifier.targetObjectName);
          break;
        case "boolean": {
          if (!modifier.targetObjectName) break;
          const targetObject = includeObject(modifier.targetObjectName);
          if (!targetObject || visitedStacks.has(targetObject.name)) break;
          visitedStacks.add(targetObject.name);
          const overrides = sceneObjectModifierOverrides?.get(targetObject.id);
          if (!overrides || overrides.length === 0) break;
          visitStack(mergeModifierStack([], overrides));
          break;
        }
      }
    }
  };

  visitStack(modifiers);

  return {
    sceneObjectsByName: collectedObjects,
    modifierOverrides: collectedOverrides,
    sceneObjectCacheKeys: collectedCacheKeys,
  };
}

export function resolveModifierEvaluation(
  owner: ModifierOwnerTransform,
  modifiers: readonly BlendModifier[],
  sceneObjects: ReadonlyMap<string, SceneObject>,
  /**
   * Modifier-override list for each scene object, keyed by `SceneObject.id`
   * (matching `StoreState.modifierOverrides`). When the instance modifier
   * resolves a source object, its own modifier stack (looked up here) is
   * applied to the source's base geometry so the instances reflect post-
   * stack effects (noise, subsurf, mirror, …). When omitted, source geometry
   * falls back to the raw base mesh — i.e. the pre-fix behavior.
   */
  sceneObjectModifierOverrides?: ReadonlyMap<
    string,
    readonly ModifierOverride[]
  >,
  /**
   * Names of source objects currently being resolved further up the call
   * stack. Used to break cycles when an instance modifier chain loops back
   * on itself (A → instance of B → instance of A). A visited source falls
   * back to its unmodified base geometry rather than recursing forever.
   */
  visitedSourceNames?: ReadonlySet<string>,
  sceneObjectCacheKeys?: ReadonlyMap<string, string>,
  runtimeSourceGeometries?: Record<string, ModifierGeometry>,
): { modifiers: BlendModifier[]; input?: ModifierEvaluationInput } {
  const resolved: BlendModifier[] = [];
  const sourceGeometries: Record<string, ModifierGeometry> = {};
  const curveTargets: Record<string, ModifierCurvePath> = {};
  const cacheDeps: string[] = [];
  const ownerMatrix = mat4Compose(owner.position, owner.rotation, owner.scale);
  const ownerInverseMatrix = mat4Inverse(ownerMatrix);
  let hasInputs = false;
  let needsOwnerInverseRotation = false;

  for (const modifier of modifiers) {
    if (modifier.kind === "array" && modifier.offsetObjectName) {
      const offsetObject = sceneObjects.get(modifier.offsetObjectName);
      if (!offsetObject) {
        resolved.push({ ...modifier, offsetMatrix: null });
        continue;
      }
      const offsetMatrix = mat4Compose(
        offsetObject.position,
        offsetObject.rotation,
        offsetObject.scale,
      );
      resolved.push({
        ...modifier,
        offsetMatrix: mat4Multiply(ownerInverseMatrix, offsetMatrix),
      });
      cacheDeps.push(
        `array:${modifier.offsetObjectName}:${sceneObjectCacheFragment(offsetObject, sceneObjectCacheKeys)}`,
      );
      continue;
    }

    if (modifier.kind === "instance") {
      if (modifier.sourceObjectName) {
        const sourceObject = sceneObjects.get(modifier.sourceObjectName);
        if (sourceObject) {
          let sourceGeometry = buildSceneObjectModifierGeometry(sourceObject);
          if (sourceGeometry) {
            // Populate material groups from the source object's slot assignments
            // so the instance core can duplicate them across all copies. Groups
            // are applied BEFORE the source's modifier stack runs so each core
            // can remap/preserve them through topology changes (subsurf, etc.).
            const sourceFaceMat = resolveFaceMaterialIndices(sourceObject);
            if (sourceFaceMat) {
              sourceGeometry.groups =
                buildGeometryGroupsFromFaceMaterialIndices(
                  sourceFaceMat,
                  sourceObject.topology,
                );
            }

            // Evaluate the source object's own modifier stack so instances
            // reflect post-stack geometry (noise, subsurf, mirror, solidify, …)
            // rather than the raw base mesh. Scene object modifiers live in
            // the store's `modifierOverrides` map keyed by object id — the
            // SceneObject struct itself has no `.modifiers` field, so the
            // overrides lookup must be supplied by the caller for the fix
            // to take effect. Guarded by `visitedSourceNames` to break
            // instance-modifier cycles (A → B → A). The source object is
            // resolved with its OWN transform as the owner so any vertex
            // groups referenced by its modifiers resolve against the
            // source's group list, matching the renderer path for that
            // object.
            const sourceOverrides = sceneObjectModifierOverrides?.get(
              sourceObject.id,
            );
            const sourceStack = sourceOverrides
              ? mergeModifierStack([], sourceOverrides)
              : [];
            let sourceStackKey = "";
            if (
              sourceStack.length > 0 &&
              !visitedSourceNames?.has(modifier.sourceObjectName)
            ) {
              const nextVisited = new Set(visitedSourceNames ?? []);
              nextVisited.add(modifier.sourceObjectName);
              const sourceResolved = resolveModifierEvaluation(
                {
                  position: sourceObject.position,
                  rotation: sourceObject.rotation,
                  scale: sourceObject.scale,
                  vertexGroups: sourceObject.vertexGroups,
                },
                sourceStack,
                sceneObjects,
                sceneObjectModifierOverrides,
                nextVisited,
                sceneObjectCacheKeys,
                runtimeSourceGeometries,
              );
              sourceGeometry = applyModifiers(
                sourceGeometry,
                sourceResolved.modifiers,
                sourceResolved.input,
                // Keep the source object's grouped instance variant separate
                // from its normal render cache entry. Reusing the plain scene
                // key here dropped material groups and hid the shaded instance.
                sceneObjectVariantCacheFragment(
                  sourceObject,
                  "instance-source",
                  sceneObjectCacheKeys,
                ),
              );
              // Fold the source's effective stack signature into the instance
              // modifier's cache key so that edits on the source's stack
              // (e.g. bumping a noise layer's strength) invalidate the cached
              // instance output even when the SceneObject reference is reused.
              sourceStackKey = modifierStackKey(
                sourceResolved.modifiers,
                sourceResolved.input,
              );
            }

            sourceGeometries[modifier.sourceObjectName] = sourceGeometry;
            hasInputs = true;
            cacheDeps.push(
              `instance:${modifier.sourceObjectName}:${sceneObjectCacheFragment(sourceObject, sceneObjectCacheKeys)}:${sourceStackKey}`,
            );
          }
        }
      }
      if (modifier.orientation === "global") {
        needsOwnerInverseRotation = true;
      }
      // Runtime-resolve the optional vertex group reference into a flat index
      // array. Mirrors the Array modifier's `offsetMatrix` runtime-resolved
      // field pattern. Null when unset or when the referenced group no longer
      // exists (graceful fallback to whole-mesh placement).
      const vertexGroupIndices = resolveVertexGroupIndices(
        owner,
        modifier.vertexGroupId,
      );
      if (vertexGroupIndices) {
        cacheDeps.push(
          `instance-vg:${modifier.vertexGroupId}:${vertexGroupIndices.length}`,
        );
      }
      resolved.push({ ...modifier, vertexGroupIndices });
      continue;
    }

    if (modifier.kind === "shrinkwrap") {
      if (modifier.targetObjectName) {
        const targetObject = sceneObjects.get(modifier.targetObjectName);
        if (targetObject) {
          const targetGeo =
            runtimeSourceGeometries?.[modifier.targetObjectName] ??
            buildSceneObjectModifierGeometry(targetObject);
          // Transform target positions from target-local → owner-local space so
          // the core can compare source vertices (owner-local) against the target
          // surface (now also owner-local) without needing world-space transforms.
          const targetMatrix = mat4Compose(
            targetObject.position,
            targetObject.rotation,
            targetObject.scale,
          );
          const targetToOwner = mat4Multiply(ownerInverseMatrix, targetMatrix);
          sourceGeometries[modifier.targetObjectName] = {
            ...targetGeo,
            positions: mat4TransformPositions(
              targetGeo.positions,
              targetToOwner,
            ),
            normals: mat4TransformNormals(targetGeo.normals, targetToOwner),
          };
          hasInputs = true;
          cacheDeps.push(
            `shrinkwrap:${modifier.targetObjectName}:${sceneObjectCacheFragment(targetObject, sceneObjectCacheKeys)}`,
          );
        }
      }
      const vertexGroupIndices = resolveVertexGroupIndices(
        owner,
        modifier.vertexGroupId,
      );
      resolved.push({ ...modifier, vertexGroupIndices });
      continue;
    }

    if (modifier.kind === "curve") {
      if (modifier.targetObjectName) {
        const targetObject = sceneObjects.get(modifier.targetObjectName);
        if (targetObject?.curveData) {
          const curvePath = buildCurveTargetPath(
            ownerInverseMatrix,
            targetObject,
          );
          if (curvePath) {
            curveTargets[modifier.targetObjectName] = curvePath;
            hasInputs = true;
            cacheDeps.push(
              `curve:${modifier.targetObjectName}:${sceneObjectCacheFragment(targetObject, sceneObjectCacheKeys)}`,
            );
          }
        }
      }
      resolved.push(modifier);
      continue;
    }

    if (modifier.kind === "boolean") {
      if (modifier.targetObjectName) {
        const targetObject = sceneObjects.get(modifier.targetObjectName);
        // Self-cut is prevented at the UI layer (the target picker hides the
        // owner object) so we don't re-check here. If a stale config does
        // point at the owner, the core degrades gracefully.
        if (targetObject) {
          // Evaluate the cutter's own modifier stack so a mirrored / subsurfed
          // cutter cuts as it appears in the viewport. Mirrors the pattern
          // used by the Instance branch above; cycle-guarded via
          // `visitedSourceNames`.
          let cutterGeometry = buildSceneObjectModifierGeometry(targetObject);
          const sourceOverrides = sceneObjectModifierOverrides?.get(
            targetObject.id,
          );
          const sourceStack = sourceOverrides
            ? mergeModifierStack([], sourceOverrides)
            : [];
          let sourceStackKey = "";
          if (
            sourceStack.length > 0 &&
            !visitedSourceNames?.has(modifier.targetObjectName)
          ) {
            const nextVisited = new Set(visitedSourceNames ?? []);
            nextVisited.add(modifier.targetObjectName);
            const sourceResolved = resolveModifierEvaluation(
              {
                position: targetObject.position,
                rotation: targetObject.rotation,
                scale: targetObject.scale,
                vertexGroups: targetObject.vertexGroups,
              },
              sourceStack,
              sceneObjects,
              sceneObjectModifierOverrides,
              nextVisited,
              sceneObjectCacheKeys,
              runtimeSourceGeometries,
            );
            cutterGeometry = applyModifiers(
              cutterGeometry,
              sourceResolved.modifiers,
              sourceResolved.input,
              sceneObjectCacheFragment(targetObject, sceneObjectCacheKeys),
            );
            sourceStackKey = modifierStackKey(
              sourceResolved.modifiers,
              sourceResolved.input,
            );
          }
          // Transform post-stack cutter positions from cutter-local → owner-local.
          const cutterMatrix = mat4Compose(
            targetObject.position,
            targetObject.rotation,
            targetObject.scale,
          );
          const cutterToOwner = mat4Multiply(ownerInverseMatrix, cutterMatrix);
          sourceGeometries[modifier.targetObjectName] = {
            ...cutterGeometry,
            positions: mat4TransformPositions(
              cutterGeometry.positions,
              cutterToOwner,
            ),
          };
          hasInputs = true;
          cacheDeps.push(
            `boolean:${modifier.targetObjectName}:${sceneObjectCacheFragment(targetObject, sceneObjectCacheKeys)}:${sourceStackKey}`,
          );
        }
      }
      resolved.push(modifier);
      continue;
    }

    if (
      modifier.kind === "solidify" ||
      modifier.kind === "skin" ||
      modifier.kind === "smooth" ||
      modifier.kind === "bevel"
    ) {
      // Runtime-resolve the optional vertex group reference into a flat index
      // array, following the same pattern as the Instance modifier. The resolved
      // indices are stored directly on the modifier struct so the core can read
      // them without needing the `input` parameter.
      const vertexGroupIndices = resolveVertexGroupIndices(
        owner,
        modifier.vertexGroupId,
      );
      resolved.push({ ...modifier, vertexGroupIndices });
      continue;
    }

    if (modifier.kind === "noise") {
      // Each noise layer has its own optional vertex group; resolve them
      // individually so the core can apply per-layer masks without re-walking
      // the owner's group list every frame. Layers with no group or a missing
      // group get `vertexGroupIndices: null` → "apply to whole mesh".
      const resolvedLayers = modifier.layers.map((layer) => ({
        ...layer,
        vertexGroupIndices: resolveVertexGroupIndices(
          owner,
          layer.vertexGroupId,
        ),
      }));
      resolved.push({ ...modifier, layers: resolvedLayers });
      continue;
    }

    resolved.push(modifier);
  }

  if (!hasInputs && cacheDeps.length === 0 && !needsOwnerInverseRotation) {
    return { modifiers: resolved };
  }

  // Build rotation-only inverse for "global" orientation: compose with
  // unit scale and zero translation, then invert.
  let ownerInverseRotation: number[] | undefined;
  if (needsOwnerInverseRotation) {
    const rotOnly = mat4Compose([0, 0, 0], owner.rotation, [1, 1, 1]);
    ownerInverseRotation = mat4Inverse(rotOnly);
  }

  return {
    modifiers: resolved,
    input: {
      sourceGeometries:
        Object.keys(sourceGeometries).length > 0 ? sourceGeometries : undefined,
      curveTargets:
        Object.keys(curveTargets).length > 0 ? curveTargets : undefined,
      cacheKey: cacheDeps.length > 0 ? cacheDeps.join("|") : undefined,
      ownerInverseRotation,
    },
  };
}
